/**
 * Mantine theme for Dovach. Used by all three web apps.
 *
 * Colour philosophy: a single warm-blue accent, restrained chroma elsewhere.
 * Israeli accountancy software (`חשבשבת`, `ריווחית`) is dense and saturated;
 * we deliberately go quieter so Dovach reads as "modern, calm, trustworthy"
 * to clients about to upload personal financial documents — and as a clean
 * power-user surface to accountants who spend all day in their bookkeeping app.
 *
 * Typography defaults to system fonts to keep first-paint fast and to render
 * Hebrew well across all platforms — bundling a custom Hebrew font is a real
 * cost (60–200 KB extra) for negligible visual upside in v1.
 */

import { createTheme, type MantineColorsTuple, type MantineThemeOverride } from "@mantine/core";

// Brand: warm blue, weighted toward the indigo end of the spectrum so it reads
// "official" rather than "playful". 10-step Mantine tuple.
const brand: MantineColorsTuple = [
  "#eef3ff",
  "#dde6ff",
  "#b9caff",
  "#92acff",
  "#7193ff",
  "#5d83ff",
  "#527afe",
  "#4368e3",
  "#385cca",
  "#2a4fb4",
];

// Accent: muted teal, used for "received / success" states alongside green.
const accent: MantineColorsTuple = [
  "#e8fbf7",
  "#d2f2ec",
  "#a3e3d4",
  "#71d3bc",
  "#4cc6a8",
  "#37bf9b",
  "#28bb95",
  "#15a481",
  "#019272",
  "#007e60",
];

export const dovachDarkColors: MantineColorsTuple = [
  "#cbd2db",
  "#a1abb9",
  "#737d8c",
  "#4a5260",
  "#373d48",
  "#2b303a",
  "#22262e",
  "#191c22",
  "#101218",
  "#08090c",
];

export const dovachTheme: MantineThemeOverride = createTheme({
  primaryColor: "brand",
  primaryShade: { light: 6, dark: 7 },
  fontFamily:
    "system-ui, -apple-system, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, " +
    "'Noto Sans Hebrew', 'Frank Ruehl', sans-serif",
  fontFamilyMonospace: "ui-monospace, 'Cascadia Code', 'Source Code Pro', monospace",
  defaultRadius: "md",
  cursorType: "pointer",
  colors: {
    brand,
    accent,
    dark: dovachDarkColors,
  },
  components: {
    // Default to button labels that don't UPPERCASE — Hebrew has no case.
    Button: {
      defaultProps: { variant: "filled" },
    },
    // Inputs: sensible default sizing so accountants on dense dashboards
    // and 65-year-old clients on tiny phones both get acceptable hit areas.
    TextInput: {
      defaultProps: { size: "md" },
    },
  },
  // Mantine 7 RTL is opt-in per <DirectionProvider>. Apps wrap their root in
  // <DirectionProvider initialDirection="rtl"> — see app shells.
});
